/**
 * How a Kraken limb physically takes a ship — one description, evaluated by the mesh
 * AND the gun.
 *
 * Lives in content/ rather than beside the mesh builder because the simulation has to
 * agree with it: the limb's hit volume follows the same wrap the renderer draws, so a
 * limb coiled over a hull is shootable where it visibly is. Pure math only.
 *
 * The simulation already owns the important truth: a tentacle names its target and
 * repeatedly damages that hull until the ordinary sinking state takes over. These pure
 * helpers turn that state into visible contact, list, lift, crush and drag without
 * changing a single gameplay outcome.
 */

import type { Vessel } from '../sim/types';
import { vesselSpec } from './vessels';

export interface KrakenPredationPose {
  /** Combined hold of every limb on this hull, 0..1. */
  contact: number;
  /** Hull-space compression, 0..1. */
  crush: number;
  /** Final pull below the surface, 0..1. */
  drag: number;
  /** Extra world-space translation toward the attacking limbs. */
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  /** Extra pitch/list applied on top of wave buoyancy. */
  pitch: number;
  roll: number;
  /** Number of limbs contributing to the pose. */
  attackerCount: number;
}

export interface KrakenTentacleGrip {
  grip: number;
  /** Target point expressed in the tentacle's local XZ frame. */
  localX: number;
  localZ: number;
  /** Target height above the tentacle root. */
  localY: number;
  /** Slow contraction used by the wrapping portion of the limb. */
  clutch: number;
  /** Hull station at the centre of the wrap, in the limb's local frame. */
  stationLocalX: number;
  stationLocalZ: number;
  /** Target fore/aft and starboard axes, expressed in the limb's local frame. */
  forwardLocalX: number;
  forwardLocalZ: number;
  rightLocalX: number;
  rightLocalZ: number;
  /** Anatomy needs the actual hull breadth instead of a generic contact point. */
  halfBeam: number;
  hullHeight: number;
  /** Which side of the hull this particular limb first meets. */
  side: -1 | 1;
  /** Stable anatomical difference so two attackers never form matching arches. */
  variant: number;
}

export interface KrakenWrapPoint {
  x: number;
  y: number;
  z: number;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

function liveHull(v: Vessel): boolean {
  return v.kind !== 'tentacle'
    && v.kind !== 'kraken'
    && v.state !== 'dead'
    && v.state !== 'sinking'
    && v.state !== 'departing';
}

/**
 * Resolve the hull a limb should visibly reach for.
 *
 * Authored gameplay uses `targetId`. The short proximity fallback exists for benchmark
 * scenes and also prevents a one-frame visual pop while the simulation is assigning a
 * target to a limb that surfaced already alongside a ship.
 */
export function krakenTentacleTarget(
  tentacle: Vessel,
  vessels: readonly Vessel[],
): Vessel | null {
  if (tentacle.kind !== 'tentacle' || tentacle.state === 'dead') return null;

  const assigned = tentacle.targetId === null
    ? null
    : vessels.find((v) => v.id === tentacle.targetId && liveHull(v)) ?? null;
  if (assigned) return assigned;

  let nearest: Vessel | null = null;
  let nearestDistance = 116;
  for (const vessel of vessels) {
    if (!liveHull(vessel)) continue;
    const distance = Math.hypot(
      vessel.pos.x - tentacle.pos.x,
      vessel.pos.z - tentacle.pos.z,
    );
    if (distance >= nearestDistance) continue;
    nearest = vessel;
    nearestDistance = distance;
  }
  return nearest;
}

/** How a limb bends from its planted root to the target hull. */
export function krakenTentacleGrip(
  tentacle: Vessel,
  target: Vessel,
  simTime: number,
): KrakenTentacleGrip {
  const dx = target.pos.x - tentacle.pos.x;
  const dz = target.pos.z - tentacle.pos.z;
  const distance = Math.hypot(dx, dz);
  const assigned = tentacle.targetId === target.id;
  const reach = assigned ? 145 : 116;
  const inner = assigned ? 74 : 58;
  const grip = smoothstep((reach - distance) / Math.max(1, reach - inner));

  const spec = vesselSpec(target.kind);
  const targetForwardX = Math.sin(target.heading);
  const targetForwardZ = Math.cos(target.heading);
  const targetRightX = Math.cos(target.heading);
  const targetRightZ = -Math.sin(target.heading);
  const side: -1 | 1 = tentacle.id % 2 === 0 ? 1 : -1;
  const station = (tentacle.id % 3) - 1;
  const stationX = target.pos.x + targetForwardX * station * spec.length * 0.22;
  const stationZ = target.pos.z + targetForwardZ * station * spec.length * 0.22;
  const contactX = stationX + targetRightX * side * spec.beam * 0.42;
  const contactZ = stationZ + targetRightZ * side * spec.beam * 0.42;
  const contactDx = contactX - tentacle.pos.x;
  const contactDz = contactZ - tentacle.pos.z;
  const stationDx = stationX - tentacle.pos.x;
  const stationDz = stationZ - tentacle.pos.z;
  const c = Math.cos(tentacle.heading);
  const s = Math.sin(tentacle.heading);
  const localX = c * contactDx - s * contactDz;
  const localZ = s * contactDx + c * contactDz;
  const stationLocalX = c * stationDx - s * stationDz;
  const stationLocalZ = s * stationDx + c * stationDz;
  const clutch = 0.78 + Math.sin(simTime * 0.41 + tentacle.id * 1.73) * 0.22;

  return {
    grip,
    localX,
    localZ,
    localY: target.pos.y - tentacle.pos.y + Math.min(14, spec.height * 0.45),
    clutch,
    stationLocalX,
    stationLocalZ,
    forwardLocalX: c * targetForwardX - s * targetForwardZ,
    forwardLocalZ: s * targetForwardX + c * targetForwardZ,
    rightLocalX: c * targetRightX - s * targetRightZ,
    rightLocalZ: s * targetRightX + c * targetRightZ,
    halfBeam: spec.beam * 0.5,
    hullHeight: spec.height,
    side,
    variant: ((tentacle.id * 37) % 11) / 5 - 1,
  };
}

/**
 * A continuous muscular path around the real breadth of the target hull.
 *
 * The old deformation ended every slice at one contact point. That produced a straight
 * upright tube beside a ship. This path instead has four readable beats: a planted root,
 * a heavy rising shoulder, contact on the near rail, an arch over the deck, and a hook
 * down the far side. A quartic Bézier joins them without hinges.
 */
export function krakenTentacleWrapPoint(
  grip: KrakenTentacleGrip,
  progress: number,
): KrakenWrapPoint {
  const t = clamp01(progress);
  const s = 1 - t;
  const distance = Math.max(1, Math.hypot(grip.stationLocalX, grip.stationLocalZ));
  const approachSideX = -grip.stationLocalZ / distance;
  const approachSideZ = grip.stationLocalX / distance;
  const shoulderSweep = grip.side * (17 + grip.halfBeam * 0.42) * grip.clutch;
  const crownY = grip.localY
    + 4
    + Math.min(4, grip.hullHeight * 0.12)
    + grip.halfBeam * 0.05
    + grip.variant * 10;

  const p0 = { x: 0, y: -30, z: 0 };
  const p1 = {
    x: grip.stationLocalX * 0.60 + approachSideX * shoulderSweep,
    y: 32 + Math.min(10, grip.hullHeight * 0.25) + grip.variant * 14,
    z: grip.stationLocalZ * 0.60 + approachSideZ * shoulderSweep,
  };
  const p2 = {
    x: grip.stationLocalX + grip.rightLocalX * grip.side * grip.halfBeam * 1.45,
    y: crownY - 5,
    z: grip.stationLocalZ + grip.rightLocalZ * grip.side * grip.halfBeam * 1.45,
  };
  const p3 = {
    x: grip.stationLocalX
      + grip.rightLocalX * grip.side * grip.halfBeam * 0.28
      + grip.forwardLocalX * grip.variant * grip.halfBeam * 0.52
      + grip.forwardLocalX * (grip.clutch - 0.78) * grip.halfBeam * 0.45,
    y: crownY + 4,
    z: grip.stationLocalZ
      + grip.rightLocalZ * grip.side * grip.halfBeam * 0.28
      + grip.forwardLocalZ * grip.variant * grip.halfBeam * 0.52
      + grip.forwardLocalZ * (grip.clutch - 0.78) * grip.halfBeam * 0.45,
  };
  const p4 = {
    x: grip.stationLocalX - grip.rightLocalX * grip.side * grip.halfBeam * 0.82,
    y: grip.localY - 7 - grip.halfBeam * 0.24 + grip.variant * 4,
    z: grip.stationLocalZ - grip.rightLocalZ * grip.side * grip.halfBeam * 0.82,
  };

  const w0 = s * s * s * s;
  const w1 = 4 * s * s * s * t;
  const w2 = 6 * s * s * t * t;
  const w3 = 4 * s * t * t * t;
  const w4 = t * t * t * t;
  return {
    x: p0.x * w0 + p1.x * w1 + p2.x * w2 + p3.x * w3 + p4.x * w4,
    y: p0.y * w0 + p1.y * w1 + p2.y * w2 + p3.y * w3 + p4.y * w4,
    z: p0.z * w0 + p1.z * w1 + p2.z * w2 + p3.z * w3 + p4.z * w4,
  };
}

/**
 * Visible consequence on the captured hull.
 *
 * Contact supplies the first unmistakable list and lift. Actual HP loss then deepens
 * the roll, bows the hull toward the grip, compresses its silhouette and finally pulls
 * it under. Once the simulation enters `sinking`, this pose disappears and the normal
 * long-form wreck animation takes over.
 */
export function krakenPredationPose(
  victim: Vessel,
  vessels: readonly Vessel[],
  simTime: number,
): KrakenPredationPose | null {
  if (!liveHull(victim)) return null;

  const grips: Array<{ tentacle: Vessel; weight: number }> = [];
  for (const tentacle of vessels) {
    if (tentacle.kind !== 'tentacle') continue;
    if (krakenTentacleTarget(tentacle, vessels)?.id !== victim.id) continue;
    const grip = krakenTentacleGrip(tentacle, victim, simTime);
    if (grip.grip > 0.01) grips.push({ tentacle, weight: grip.grip });
  }
  if (grips.length === 0) return null;

  let totalWeight = 0;
  let centreX = 0;
  let centreZ = 0;
  for (const grip of grips) {
    totalWeight += grip.weight;
    centreX += grip.tentacle.pos.x * grip.weight;
    centreZ += grip.tentacle.pos.z * grip.weight;
  }
  centreX /= totalWeight;
  centreZ /= totalWeight;

  const contact = clamp01(totalWeight * 0.72);
  const damage = clamp01(1 - victim.hp / Math.max(1, victim.maxHp));
  const crush = smoothstep((damage - 0.22) / 0.58) * contact;
  const drag = smoothstep((damage - 0.66) / 0.34) * contact;
  const dx = centreX - victim.pos.x;
  const dz = centreZ - victim.pos.z;
  const distance = Math.hypot(dx, dz) || 1;
  const tug = contact * (9 + crush * 18);
  const lift = contact * (8 + smoothstep(damage / 0.55) * 16) * (1 - drag);
  const primary = grips.reduce(
    (best, next) => next.weight > best.weight ? next : best,
    grips[0] as { tentacle: Vessel; weight: number },
  );
  const side = primary.tentacle.id % 2 === 0 ? 1 : -1;
  const heave = 0.86 + Math.sin(simTime * 0.29 + primary.tentacle.id) * 0.14;

  return {
    contact,
    crush,
    drag,
    offsetX: (dx / distance) * tug,
    offsetY: lift - drag * 30,
    offsetZ: (dz / distance) * tug,
    pitch: side * contact * (0.08 + crush * 0.24 + drag * 0.16) * heave,
    // First contact lists the hull enough to prove weight without hiding it behind its
    // own deck. The violent capsize belongs to accumulated damage, not frame one.
    roll: side * contact * (0.38 + crush * 1.04 + drag * 0.50) * heave,
    attackerCount: grips.length,
  };
}
